/**
 * Starting social sign-in from wherever the visitor happens to be.
 *
 * A company advisor is served from its own hostname — acme.aibrokerhub.com —
 * and that hostname cannot be a Google or Facebook redirect URI: both match
 * redirect_uri exactly, neither supports a wildcard, and a brokerage's
 * subdomain is created without anyone opening a developer console. The old
 * `${window.location.origin}/auth/google/callback` therefore failed with
 * redirect_uri_mismatch on every branded address.
 *
 * So the browser is handed to the backend instead, which owns the one
 * registered URI per provider and carries this page's origin through the
 * provider in `state`. We come back to the same address we left, signed in —
 * the session arrives in the refresh cookie, which AuthContext picks up with
 * the silent refresh it already runs on mount. Nothing secret is in the URL.
 *
 * A full page navigation, not a fetch: consent is a top-level redirect to a
 * third party, so there is no XHR to make.
 */
import { BACKEND_URL } from '../environment';

/**
 * Leave for the provider's consent screen.
 *
 * @param {'google'|'facebook'} provider
 * @param {string} returnPath in-app path to land on afterwards, e.g. '/saved'
 */
export function startOAuth(provider, returnPath = '/') {
  const path = returnPath && returnPath.startsWith('/') ? returnPath : '/';
  const returnTo = `${window.location.origin}${path}`;
  window.location.href =
    `${BACKEND_URL}/auth/oauth/${provider}/start` +
    `?return_to=${encodeURIComponent(returnTo)}`;
}

/** Human-readable text for the `?auth_error=` the relay sends back on failure. */
export function oauthErrorMessage(code) {
  if (!code) return '';
  return 'Sign-in with that account did not complete. Please try again.';
}

/**
 * Land the visitor where the sign-in began, now that it has succeeded.
 *
 * `returnTo` is whatever the server put in the token response after verifying
 * the relay `state` — an absolute URL, or empty for an ordinary sign-in on this
 * host. It is not read from the URL here: the server decides, because `state`
 * reaches this page through the provider.
 *
 * Crossing back to a company's own hostname is a full page load, which drops
 * the in-memory access token. That is fine and intended — the refresh cookie
 * was set on the API host during the exchange, and AuthContext's silent refresh
 * on mount turns it back into a session on the other side.
 *
 * @param {string} returnTo absolute URL from the server, or ''
 * @param {(path: string, opts: object) => void} navigate react-router navigate
 * @param {string} fallbackPath in-app path when there is no returnTo
 */
export function landAfterSignIn(returnTo, navigate, fallbackPath = '/') {
  if (!returnTo) {
    navigate(fallbackPath, { replace: true });
    return;
  }
  let target;
  try {
    target = new URL(returnTo);
  } catch {
    navigate(fallbackPath, { replace: true });
    return;
  }
  if (target.origin === window.location.origin) {
    // Same host after all — stay in the SPA and keep the token we just got.
    navigate(`${target.pathname}${target.search}` || fallbackPath, { replace: true });
    return;
  }
  window.location.replace(returnTo);
}
